import { NJButton, NJHeading, NJTab, NJTabs, NJText } from '@engie-group/fluid-design-system-react';
import { PlanSegmentedControl } from './PlanSegmentedControl';

export type PlanStep = 'buildings' | 'route' | 'phasing';
export type StepMode = 'Manuel' | 'Algorithme';
export interface WireframePlan { id: string; name: string }

export const PLAN_STEPS: { id: PlanStep; label: string }[] = [
  { id: 'buildings', label: 'Bâtiments' },
  { id: 'route', label: 'Tracé' },
  { id: 'phasing', label: 'Phasage' },
];

const MODES: StepMode[] = ['Manuel', 'Algorithme'];

/** Bloc gris : emplacement dont le contenu sera détaillé avec le PO. */
export function Slot({ children, tall = false }: { children: string; tall?: boolean }) {
  return <div className={`pnv2-plans-slot${tall ? ' pnv2-plans-slot--tall' : ''}`}><NJText scale="sm">{children}</NJText></div>;
}

export function PlanList({ plans, selected, onSelect, onCreate, compact = false, heading = true }: {
  plans: WireframePlan[];
  selected: string | null;
  onSelect: (id: string) => void;
  onCreate: () => void;
  compact?: boolean;
  heading?: boolean;
}) {
  return (
    <div className="pnv2-plans-list">
      <div className="pnv2-plans-row-head">
        {heading && <NJHeading as="h3" scale="xs">Plans de développement</NJHeading>}
        <NJText scale="xs">{plans.length} plan{plans.length > 1 ? 's' : ''}</NJText>
      </div>
      <NJButton label="Nouveau plan de développement" icon="add" scale="sm" onClick={onCreate} />
      <ul aria-label="Plans de développement existants">
        {plans.map((plan) => (
          <li key={plan.id}>
            <button type="button" className="pnv2-plans-item" aria-current={plan.id === selected ? 'true' : undefined}
              onClick={() => onSelect(plan.id)}>
              <NJText as="span" scale="sm">{plan.name}</NJText>
              {!compact && <span className="pnv2-plans-meta" aria-hidden="true" />}
            </button>
          </li>
        ))}
      </ul>
      {!compact && <NJText scale="xs">Indicateurs de chaque plan : à détailler</NJText>}
    </div>
  );
}

function StepBody({ step, mode, onMode }: { step: PlanStep; mode: StepMode; onMode: (mode: StepMode) => void }) {
  if (step === 'buildings') {
    return <Slot tall>Choix des bâtiments du plan parmi ceux du projet (carte et/ou liste) — à détailler</Slot>;
  }
  const what = step === 'route' ? 'le tracé du réseau' : 'le phasage du raccordement';
  return (
    <div className="pnv2-plans-step">
      <PlanSegmentedControl label={`Mode de définition : ${what}`} options={MODES} value={mode}
        onChange={(value) => onMode(value === 'Algorithme' ? 'Algorithme' : 'Manuel')} />
      <Slot tall>
        {mode === 'Manuel'
          ? `Définition manuelle de ${what} — outils à détailler`
          : `Paramètres et lancement de l’algorithme pour ${what} — à détailler`}
      </Slot>
    </div>
  );
}

interface EditorProps {
  plan: WireframePlan;
  step: PlanStep;
  onStep: (step: PlanStep) => void;
  modes: Record<'route' | 'phasing', StepMode>;
  onMode: (step: 'route' | 'phasing', mode: StepMode) => void;
}

/** Navigation interne par onglets FDS (variantes B et C). */
export function PlanEditorTabs({ plan, step, onStep, modes, onMode }: EditorProps) {
  return (
    <div className="pnv2-plans-editor">
      <NJHeading as="h3" scale="xs">{plan.name}</NJHeading>
      <NJTabs label={`Définition de ${plan.name}`} activeTab={step} density="stretched"
        onClickTabItem={(id) => onStep(PLAN_STEPS.find((s) => s.id === id)?.id ?? 'buildings')}>
        {PLAN_STEPS.map((s) => (
          <NJTab key={s.id} id={s.id} label={s.label} ariaControls={`plans-review-${s.id}`}>
            <div className="pnv2-plans-tabbody">
              <StepBody step={s.id} mode={s.id === 'buildings' ? 'Manuel' : modes[s.id]}
                onMode={(m) => { if (s.id !== 'buildings') onMode(s.id, m); }} />
            </div>
          </NJTab>
        ))}
      </NJTabs>
    </div>
  );
}

/** Navigation interne par étapes numérotées dépliables (variante A). */
export function PlanEditorSteps({ plan, step, onStep, modes, onMode, onBack }: EditorProps & { onBack: () => void }) {
  return (
    <div className="pnv2-plans-editor">
      <NJButton label="Tous les plans" icon="arrow_back" variant="secondary" emphasis="minimal" scale="sm" onClick={onBack} />
      <NJHeading as="h3" scale="xs">{plan.name}</NJHeading>
      <ol className="pnv2-plans-steps">
        {PLAN_STEPS.map((s, index) => (
          <li key={s.id}>
            <button type="button" className="pnv2-plans-stephead" aria-expanded={step === s.id} onClick={() => onStep(s.id)}>
              <NJText as="span" scale="sm">{index + 1}. {s.label}</NJText>
              <span className="pnv2-plans-meta" aria-hidden="true" />
            </button>
            {step === s.id && (
              <StepBody step={s.id} mode={s.id === 'buildings' ? 'Manuel' : modes[s.id]}
                onMode={(m) => { if (s.id !== 'buildings') onMode(s.id, m); }} />
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

export function MapBlock({ plan, step }: { plan: WireframePlan | null; step: PlanStep }) {
  const label = PLAN_STEPS.find((s) => s.id === step)?.label ?? '';
  return (
    <div className="pnv2-plans-map" role="img"
      aria-label={plan ? `Carte du projet · ${plan.name} · étape ${label}` : 'Carte du projet'}>
      <NJText as="strong">Carte du projet</NJText>
      <NJText scale="sm">
        {plan ? `${plan.name} · affichage de l’étape « ${label} »` : 'Bâtiments du projet · aucun plan choisi'}
      </NJText>
    </div>
  );
}
